import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Loader2, Plus, Pencil, Trash2, Gauge, Glasses, CircleDot, ScanEye, Images, AlertTriangle, Eye, Syringe, X, Brush,
} from 'lucide-react';
import {
  CLASES_REFRACCION, COLOR_OJO, FARMACOS_INTRAVITREOS, METODOS_PIO, OJOS, av, dec, dpt, fechaCorta, num, refraccionTexto, ultimo,
} from '../../lib/oftalmo';
import GraficaOjos from './GraficaOjos';
import RegistroDialog from './RegistroDialog';
import ImagenesOftalmo from './ImagenesOftalmo';
import DibujosOculares from './DibujoOcular';
import NotaConsulta from './NotaConsulta';
import EntradaRapida from './EntradaRapida';
import InformeOftalmo from './InformeOftalmo';
import { DatosClinicosCard } from '../patient/HistoriaClinicaTab';

const Ojo = ({ o }) => (
  <span className="inline-block text-[11px] font-bold px-1.5 rounded text-white" style={{ background: COLOR_OJO[o] || '#64748B' }}>{o}</span>
);

function Seccion({ titulo, acciones, children, testId }) {
  return (
    <div className="cp-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-display font-semibold text-[#13231E] dark:text-foreground">{titulo}</h3>
        <div className="flex flex-wrap gap-2">{acciones}</div>
      </div>
      {children}
    </div>
  );
}

/** Tabla de registros con su edición. `cols` = [[título, (r) => celda]]. */
function Tabla({ filas, cols, puedeEditar, onEditar, onBorrar, vacio = 'Sin registros todavía.' }) {
  if (!filas.length) return <div className="text-sm text-muted-foreground py-4">{vacio}</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b">
            {cols.map(([t]) => <th key={t} className="py-1.5 pr-3 font-medium whitespace-nowrap">{t}</th>)}
            {puedeEditar && <th />}
          </tr>
        </thead>
        <tbody>
          {filas.map((r) => (
            <tr key={r.id} className="border-b last:border-0 align-top">
              {cols.map(([t, f]) => <td key={t} className="py-1.5 pr-3 whitespace-nowrap">{f(r)}</td>)}
              {puedeEditar && (
                <td className="py-1 text-right whitespace-nowrap">
                  <button type="button" className="p-1 text-muted-foreground hover:text-foreground" onClick={() => onEditar(r)} aria-label="Editar"><Pencil className="w-3.5 h-3.5" /></button>
                  <button type="button" className="p-1 text-muted-foreground hover:text-red-600" onClick={() => onBorrar(r)} aria-label="Borrar"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const porFechaDesc = (a, b) => (b.fecha + (b.created_at || '')).localeCompare(a.fecha + (a.created_at || ''));

// ---------------------------------------------------------------------------
// Ficha: diagnósticos, tratamientos y PIO objetivo
// ---------------------------------------------------------------------------
function FichaDialog({ open, onOpenChange, pid, ficha, onGuardada }) {
  const [f, setF] = useState(ficha);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { if (open) setF(JSON.parse(JSON.stringify(ficha))); }, [open, ficha]);
  if (!f) return null;
  const lista = (clave, nueva) => ({
    add: () => setF({ ...f, [clave]: [...(f[clave] || []), nueva] }),
    set: (i, k, v) => setF({ ...f, [clave]: f[clave].map((x, j) => (j === i ? { ...x, [k]: v } : x)) }),
    del: (i) => setF({ ...f, [clave]: f[clave].filter((_, j) => j !== i) }),
  });
  const dx = lista('diagnosticos', { texto: '', ojo: 'AO', desde: '' });
  const tx = lista('tratamientos', { farmaco: '', ojo: 'AO', pauta: '', desde: '' });
  const selOjo = (v, on) => (
    <select value={v} onChange={(e) => on(e.target.value)} className="h-9 rounded-md border border-input bg-background px-1 text-sm">
      {['AO', 'OD', 'OI'].map((o) => <option key={o}>{o}</option>)}
    </select>
  );
  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await api.put(`/api/patients/${pid}/oftalmologia/ficha`, f);
      toast.success('Ficha oftalmológica guardada');
      onGuardada(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Ficha oftalmológica</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <div className="font-semibold mb-1">Diagnósticos</div>
            {(f.diagnosticos || []).map((d, i) => (
              <div key={d.id || i} className="flex gap-2 mb-1.5">
                <Input value={d.texto} placeholder="Glaucoma primario de ángulo abierto…" onChange={(e) => dx.set(i, 'texto', e.target.value)} className="h-9" />
                {selOjo(d.ojo, (v) => dx.set(i, 'ojo', v))}
                <Input type="date" value={d.desde || ''} onChange={(e) => dx.set(i, 'desde', e.target.value)} className="h-9 w-40" />
                <Button variant="ghost" size="icon" onClick={() => dx.del(i)}><X className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={dx.add}><Plus className="w-4 h-4 mr-1" />Diagnóstico</Button>
          </div>
          <div>
            <div className="font-semibold mb-1">Tratamientos oftalmológicos</div>
            {(f.tratamientos || []).map((t, i) => (
              <div key={t.id || i} className="flex gap-2 mb-1.5">
                <Input value={t.farmaco} placeholder="Latanoprost 0,005 %" onChange={(e) => tx.set(i, 'farmaco', e.target.value)} className="h-9" />
                {selOjo(t.ojo, (v) => tx.set(i, 'ojo', v))}
                <Input value={t.pauta} placeholder="1 gota/noche" onChange={(e) => tx.set(i, 'pauta', e.target.value)} className="h-9 w-40" />
                <Button variant="ghost" size="icon" onClick={() => tx.del(i)}><X className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={tx.add}><Plus className="w-4 h-4 mr-1" />Tratamiento</Button>
          </div>
          <div className="flex gap-3">
            {OJOS.map(([o, O]) => (
              <label key={o} className="text-xs text-muted-foreground flex flex-col gap-1">PIO objetivo {O} (mmHg)
                <Input inputMode="decimal" className="h-9 w-32" value={f.pio_objetivo?.[o] ?? ''}
                  onChange={(e) => setF({ ...f, pio_objetivo: { ...(f.pio_objetivo || {}), [o]: e.target.value } })} />
              </label>
            ))}
          </div>
          <label className="text-xs text-muted-foreground flex flex-col gap-1">Antecedentes oftalmológicos
            <Textarea rows={2} value={f.antecedentes || ''} onChange={(e) => setF({ ...f, antecedentes: e.target.value })} />
          </label>
          <label className="text-xs text-muted-foreground flex flex-col gap-1">Notas
            <Textarea rows={2} value={f.notas || ''} onChange={(e) => setF({ ...f, notas: e.target.value })} />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando}>{guardando && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Avisos: lo que conviene mirar hoy
// ---------------------------------------------------------------------------
export function avisosOftalmo(datos) {
  const avisos = [];
  const regs = datos?.registros || [];
  const obj = datos?.ficha?.pio_objetivo || {};
  const pio = ultimo(regs, 'pio');
  OJOS.forEach(([o, O]) => {
    const v = num(pio?.[o]?.valor);
    if (v !== null && num(obj[o]) !== null && v > num(obj[o])) avisos.push(`PIO ${O} de ${v} mmHg por encima del objetivo (${obj[o]})`);
    else if (v !== null && v >= 24) avisos.push(`PIO ${O} de ${v} mmHg`);
    const octs = (datos?.imagenes || []).filter((i) => i.categoria === 'oct_macula' && i.ojo === O && num(i.valores?.cst) !== null)
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
    if (octs.length > 1) {
      const [p, u] = [octs.at(-2).valores.cst, octs.at(-1).valores.cst];
      if (u > p * 1.1) avisos.push(`Grosor macular ${O} sube de ${p} a ${u} µm`);
    }
    const topos = (datos?.imagenes || []).filter((i) => i.categoria === 'topografia' && i.ojo === O && num(i.valores?.kmax) !== null)
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
    if (topos.length > 1 && topos.at(-1).valores.kmax - topos.at(-2).valores.kmax >= 1) {
      avisos.push(`Kmax ${O} sube ${dec(topos.at(-1).valores.kmax - topos.at(-2).valores.kmax, 1)} D: posible progresión`);
    }
  });
  const plan = ultimo(regs, 'plan_refractivo');
  OJOS.forEach(([o]) => (plan?.[o]?.avisos || []).forEach((a) => avisos.push(`Plan refractivo ${o.toUpperCase()}: ${a}`)));
  return avisos;
}

// ---------------------------------------------------------------------------
// La pestaña
// ---------------------------------------------------------------------------
export default function OftalmologiaTab({ patient, onPatientUpdated }) {
  const pid = patient.id;
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [dialogo, setDialogo] = useState(null); // { tipo, registro, inicial }
  const [fichaAbierta, setFichaAbierta] = useState(false);
  const [sub, setSub] = useState('resumen');

  const cargar = useCallback(async () => {
    try {
      const r = await api.get(`/api/patients/${pid}/oftalmologia`);
      setDatos(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo cargar la historia oftalmológica');
    }
  }, [pid]);
  useEffect(() => { cargar(); }, [cargar]);

  const regs = useMemo(() => datos?.registros || [], [datos]);
  const de = useCallback((tipo, con = () => true) => regs.filter((r) => r.tipo === tipo && con(r)).sort(porFechaDesc), [regs]);

  if (error) return <div className="cp-card p-6 text-sm text-red-700">{error}</div>;
  if (!datos) return <div className="cp-card p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#2E6E5E]" /></div>;

  const puedeEditar = datos.puede_editar;
  const ficha = datos.ficha || {};
  const nuevo = (tipo, inicial) => setDialogo({ tipo, registro: null, inicial });
  const editar = (r) => setDialogo({ tipo: r.tipo, registro: r });
  const borrar = async (r) => {
    if (!window.confirm('¿Borrar este registro?')) return;
    try {
      await api.delete(`/api/patients/${pid}/oftalmologia/registros/${r.id}`);
      setDatos((d) => ({ ...d, registros: d.registros.filter((x) => x.id !== r.id) }));
      toast.success('Registro borrado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo borrar');
    }
  };
  const alGuardar = (r) => setDatos((d) => ({
    ...d, registros: d.registros.some((x) => x.id === r.id) ? d.registros.map((x) => (x.id === r.id ? r : x)) : [...d.registros, r],
  }));
  const alCambiarImagen = (c) => setDatos((d) => {
    if (c.tipo === 'nueva') return { ...d, imagenes: [...d.imagenes, c.imagen] };
    if (c.tipo === 'editada') return { ...d, imagenes: d.imagenes.map((i) => (i.id === c.imagen.id ? c.imagen : i)) };
    return { ...d, imagenes: d.imagenes.filter((i) => i.id !== c.id) };
  });
  const comun = { puedeEditar, onEditar: editar, onBorrar: borrar };
  const boton = (tipo, texto, inicial) => puedeEditar && (
    <Button size="sm" variant="outline" onClick={() => nuevo(tipo, inicial)} data-testid={`oft-nuevo-${tipo}`}><Plus className="w-4 h-4 mr-1" />{texto}</Button>
  );
  const masCampos = (tipo, inicial) => puedeEditar && (
    <Button size="sm" variant="ghost" className="text-xs text-muted-foreground" onClick={() => nuevo(tipo, inicial)} data-testid={`oft-nuevo-${tipo}`}>
      <Plus className="w-3.5 h-3.5 mr-1" />Más campos
    </Button>
  );
  // Las filas para apuntar sin abrir ventanas (4/11/2026): lo de la última
  // vez (método, tipo, fármaco) sale ya puesto.
  const rapida = (props) => puedeEditar && <EntradaRapida pid={pid} onGuardado={alGuardar} {...props} />;
  const ojoNum = (campo, ancho) => [{ k: `od.${campo}`, etiqueta: 'OD', ancho }, { k: `oi.${campo}`, etiqueta: 'OI', ancho }];

  const avisos = avisosOftalmo(datos);
  const pios = de('pio');
  const refrs = de('refraccion');
  const inys = de('inyeccion');
  const lios = de('lio');
  const imagenes = datos.imagenes || [];

  // Resumen por ojo.
  // La última refracción con esfera y, aparte, la última AV (una AV se puede
  // apuntar sola desde el resumen).
  const ultSubj = ultimo(regs, 'refraccion', (r) => ['subjetiva', 'postoperatoria', 'habitual'].includes(r.clase)
    && (num(r.od?.esf) !== null || num(r.oi?.esf) !== null));
  const ultAv = ultimo(regs, 'refraccion', (r) => ['od', 'oi'].some((o) => num(r[o]?.av_sc) !== null || num(r[o]?.av_cc) !== null));
  const ultPio = ultimo(regs, 'pio');
  const ultCv = ultimo(regs, 'campo_visual');
  const resumenOjo = (o, O) => {
    const r = ultSubj?.[o] || {};
    const a = ultAv?.[o] || {};
    const lio = ultimo(regs, 'lio', (x) => x.ojo === O && x.fase === 'implantada');
    const oct = [...imagenes.filter((i) => i.categoria === 'oct_macula' && i.ojo === O && i.valores?.cst)
      .map((i) => ({ fecha: i.fecha, cst: i.valores.cst })), ...de('oct', (x) => x[o]?.cst).map((x) => ({ fecha: x.fecha, cst: x[o].cst }))]
      .sort((a, b) => b.fecha.localeCompare(a.fecha))[0];
    const pv = num(ultPio?.[o]?.valor);
    const objetivo = num(ficha.pio_objetivo?.[o]);
    return (
      <div key={o} className="rounded-xl border p-4" style={{ borderColor: O === 'OD' ? '#FECACA' : '#BFDBFE' }} data-testid={`oft-resumen-${o}`}>
        <div className="flex items-center gap-2 mb-2"><Ojo o={O} /><span className="font-semibold">{O === 'OD' ? 'Ojo derecho' : 'Ojo izquierdo'}</span></div>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
          <dt className="text-muted-foreground">AV sc / cc</dt><dd><b>{av(a.av_sc)}</b> / <b>{av(a.av_cc)}</b> {ultAv && <span className="text-xs text-muted-foreground">({fechaCorta(ultAv.fecha)})</span>}</dd>
          <dt className="text-muted-foreground">Refracción</dt><dd>{refraccionTexto(r)} <span className="text-xs text-muted-foreground">({CLASES_REFRACCION[ultSubj?.clase]?.split(' (')[0] || '—'} {fechaCorta(ultSubj?.fecha)})</span></dd>
          <dt className="text-muted-foreground">PIO</dt>
          <dd className={pv !== null && objetivo !== null && pv > objetivo ? 'text-red-700 font-semibold' : ''}>
            {pv !== null ? `${pv} mmHg` : '—'} {objetivo !== null && <span className="text-xs text-muted-foreground">(objetivo {objetivo})</span>}
            {ultPio && <span className="text-xs text-muted-foreground"> · {fechaCorta(ultPio.fecha)}</span>}
          </dd>
          {lio && <><dt className="text-muted-foreground">LIO</dt><dd>{lio.modelo} {dpt(lio.potencia)} <span className="text-xs text-muted-foreground">({fechaCorta(lio.fecha_cirugia || lio.fecha)})</span></dd></>}
          {oct && <><dt className="text-muted-foreground">Grosor central</dt><dd>{oct.cst} µm <span className="text-xs text-muted-foreground">({fechaCorta(oct.fecha)})</span></dd></>}
          {ultCv?.[o]?.md !== null && ultCv?.[o]?.md !== undefined && <><dt className="text-muted-foreground">Campo visual</dt><dd>MD {dec(ultCv[o].md, 1)} dB · VFI {ultCv[o].vfi ?? '—'} %</dd></>}
          {inys.some((i) => i.ojo === O) && <><dt className="text-muted-foreground">Inyecciones</dt><dd>{inys.filter((i) => i.ojo === O).length} (última {fechaCorta(inys.find((i) => i.ojo === O).fecha)})</dd></>}
        </dl>
      </div>
    );
  };

  // Marcas de las gráficas.
  const cambiosTratamiento = [];
  // Una marca cuando cambia el tratamiento de base («Latanoprost» →
  // «Latanoprost + timolol») o cuando se apunta un acto («… · SLT OD»).
  [...pios].reverse().forEach((r, i, xs) => {
    if (i === 0 || !r.tratamiento) return;
    const [base, ...acto] = r.tratamiento.split('·').map((t) => t.trim());
    const basePrevia = (xs[i - 1].tratamiento || '').split('·')[0].trim();
    if (acto.length) cambiosTratamiento.push({ fecha: r.fecha, texto: acto.join(' ').slice(0, 28) });
    else if (base !== basePrevia) cambiosTratamiento.push({ fecha: r.fecha, texto: base.slice(0, 28) });
  });
  const cirugias = lios.filter((l) => l.fase === 'implantada').map((l) => ({ fecha: l.fecha_cirugia || l.fecha, texto: `LIO ${l.ojo}`, ojo: l.ojo }));
  const planes = de('plan_refractivo').filter((p) => p.fecha_cirugia).map((p) => ({ fecha: p.fecha_cirugia, texto: p.tecnica }));
  const cstPuntos = [
    ...imagenes.filter((i) => i.categoria === 'oct_macula' && num(i.valores?.cst) !== null).map((i) => ({ fecha: i.fecha, [i.ojo === 'OI' ? 'oi' : 'od']: i.valores.cst })),
    ...de('oct').filter((x) => x.od?.cst || x.oi?.cst).map((x) => ({ fecha: x.fecha, od: x.od?.cst ?? null, oi: x.oi?.cst ?? null })),
  ];
  const kmaxPuntos = [
    ...imagenes.filter((i) => i.categoria === 'topografia' && num(i.valores?.kmax) !== null).map((i) => ({ fecha: i.fecha, [i.ojo === 'OI' ? 'oi' : 'od']: i.valores.kmax })),
    ...de('topografia').filter((r) => r.od?.kmax || r.oi?.kmax).map((r) => ({ fecha: r.fecha, od: num(r.od?.kmax), oi: num(r.oi?.kmax) })),
  ];
  // Paquimetría: la mínima de las topografías (escritas o archivadas) y, si no
  // hay ninguna, la central apuntada con la PIO.
  const paquiTopo = [
    ...imagenes.filter((i) => i.categoria === 'topografia' && num(i.valores?.paqui_min) !== null).map((i) => ({ fecha: i.fecha, [i.ojo === 'OI' ? 'oi' : 'od']: i.valores.paqui_min })),
    ...de('topografia').filter((r) => r.od?.paqui_min || r.oi?.paqui_min).map((r) => ({ fecha: r.fecha, od: num(r.od?.paqui_min), oi: num(r.oi?.paqui_min) })),
  ];
  const paquiPuntos = paquiTopo.length ? paquiTopo
    : pios.filter((r) => r.od?.paquimetria || r.oi?.paquimetria).map((r) => ({ fecha: r.fecha, od: num(r.od?.paquimetria), oi: num(r.oi?.paquimetria) }));
  const avPuntos = refrs.filter((r) => num(r.od?.av_cc) !== null || num(r.oi?.av_cc) !== null)
    .map((r) => ({ fecha: r.fecha, od: num(r.od?.av_cc), oi: num(r.oi?.av_cc) }));

  return (
    <div className="space-y-4" data-testid="oftalmologia-tab">
      {/* Lo primero al entrar (4/11/2026): la consulta de hoy, que empieza por
          el motivo de consulta y su evolución, con «Grabar consulta». */}
      <NotaConsulta patient={patient} registros={regs} onRegistro={alGuardar} iaActiva={datos.ia_activa} />

      {/* Ficha */}
      <div className="cp-card p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2 min-w-0">
            <div className="flex flex-wrap gap-1.5">
              {(ficha.diagnosticos || []).length === 0 && <span className="text-sm text-muted-foreground">Sin diagnósticos oftalmológicos.</span>}
              {(ficha.diagnosticos || []).map((d) => (
                <span key={d.id} className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs" data-testid="oft-diagnostico">
                  <Ojo o={d.ojo} />{d.texto}
                </span>
              ))}
            </div>
            {(ficha.tratamientos || []).length > 0 && (
              <div className="text-xs text-muted-foreground">
                <b className="text-foreground">Tratamiento:</b> {ficha.tratamientos.map((t) => `${t.farmaco} (${t.ojo}${t.pauta ? `, ${t.pauta}` : ''})`).join(' · ')}
              </div>
            )}
            {ficha.antecedentes && <div className="text-xs text-muted-foreground"><b className="text-foreground">Antecedentes:</b> {ficha.antecedentes}</div>}
          </div>
          <div className="flex flex-wrap gap-2">
            <InformeOftalmo pid={pid} />
            {puedeEditar && <Button size="sm" variant="outline" onClick={() => setFichaAbierta(true)} data-testid="oft-editar-ficha"><Pencil className="w-4 h-4 mr-1" />Ficha</Button>}
          </div>
        </div>
        {avisos.length > 0 && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-900 dark:text-amber-200 space-y-0.5" data-testid="oft-avisos">
            {avisos.map((a) => <div key={a} className="flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 shrink-0" />{a}</div>)}
          </div>
        )}
      </div>

      {/* Lo primero al entrar (4/11/2026): alergias, cirugía ocular previa,
          medicación y antecedentes, y lo que se escribe en la consulta. */}
      <DatosClinicosCard patient={patient} onPatientUpdated={onPatientUpdated} />

      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="resumen"><Eye className="w-4 h-4 mr-1" />Resumen</TabsTrigger>
          <TabsTrigger value="pio" data-testid="oft-sub-pio"><Gauge className="w-4 h-4 mr-1" />PIO y glaucoma</TabsTrigger>
          <TabsTrigger value="refraccion" data-testid="oft-sub-refraccion"><Glasses className="w-4 h-4 mr-1" />Refracción y refractiva</TabsTrigger>
          <TabsTrigger value="lio" data-testid="oft-sub-lio"><CircleDot className="w-4 h-4 mr-1" />Biometría y LIO</TabsTrigger>
          <TabsTrigger value="cornea" data-testid="oft-sub-cornea"><Brush className="w-4 h-4 mr-1" />Córnea</TabsTrigger>
          <TabsTrigger value="retina" data-testid="oft-sub-retina"><ScanEye className="w-4 h-4 mr-1" />Mácula y retina</TabsTrigger>
          <TabsTrigger value="imagenes" data-testid="oft-sub-imagenes"><Images className="w-4 h-4 mr-1" />Imágenes ({imagenes.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="resumen" className="mt-4 space-y-4">
          {puedeEditar && (
            <div className="cp-card p-3 sm:px-5 flex flex-wrap gap-x-4" data-testid="oft-resumen-rapido">
              {rapida({ tipo: 'pio', titulo: 'Tensión ocular', unidad: 'mmHg', testId: 'oft-resumen-pio',
                campos: [...ojoNum('valor'), { k: 'metodo', etiqueta: 'Método', opciones: METODOS_PIO }],
                iniciales: { metodo: pios[0]?.metodo || 'Goldmann' } })}
              {rapida({ tipo: 'refraccion', titulo: 'Agudeza visual', testId: 'oft-resumen-av',
                campos: [{ k: 'od.av_sc', etiqueta: 'OD sc', ancho: 'w-14' }, { k: 'od.av_cc', etiqueta: 'OD cc', ancho: 'w-14' },
                  { k: 'oi.av_sc', etiqueta: 'OI sc', ancho: 'w-14' }, { k: 'oi.av_cc', etiqueta: 'OI cc', ancho: 'w-14' }],
                iniciales: {} })}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{OJOS.map(([o, O]) => resumenOjo(o, O))}</div>
          {pios.length > 1 && (
            <Seccion titulo="Tensión ocular"><GraficaOjos puntos={pios.map((r) => ({ fecha: r.fecha, od: num(r.od?.valor), oi: num(r.oi?.valor) }))}
              unidad="mmHg" objetivo={ficha.pio_objetivo} marcas={cambiosTratamiento} alto={220} /></Seccion>
          )}
          {cstPuntos.length > 1 && (
            <Seccion titulo="Grosor macular central"><GraficaOjos puntos={cstPuntos} unidad="µm" alto={220}
              marcas={inys.map((i) => ({ fecha: i.fecha, ojo: i.ojo }))} /></Seccion>
          )}
          {paquiPuntos.length > 1 && (
            <Seccion titulo={paquiTopo.length ? 'Paquimetría mínima' : 'Paquimetría'} testId="oft-resumen-paqui">
              <GraficaOjos puntos={paquiPuntos} unidad="µm" alto={220} marcas={[...cirugias, ...planes]} />
            </Seccion>
          )}
          {avPuntos.length > 1 && (
            <Seccion titulo="Agudeza visual con corrección (decimal)"><GraficaOjos puntos={avPuntos} alto={200} decimales={1} dominio={[0, 1.2]}
              marcas={[...cirugias, ...planes]} /></Seccion>
          )}
        </TabsContent>

        <TabsContent value="pio" className="mt-4 space-y-4">
          <Seccion titulo="Tensión ocular (PIO)" testId="oft-seccion-pio" acciones={masCampos('pio')}>
            {rapida({ tipo: 'pio', titulo: 'Tensión ocular', unidad: 'mmHg', testId: 'oft-rapida-pio',
              campos: [...ojoNum('valor'), { k: 'metodo', etiqueta: 'Método', opciones: METODOS_PIO }],
              iniciales: { metodo: pios[0]?.metodo || 'Goldmann' } })}
            <GraficaOjos testId="oft-grafica-pio" puntos={pios.map((r) => ({ fecha: r.fecha, od: num(r.od?.valor), oi: num(r.oi?.valor) }))}
              unidad="mmHg" objetivo={ficha.pio_objetivo} marcas={cambiosTratamiento} dominio={[0, 'auto']} />
            <Tabla filas={pios} {...comun} cols={[
              ['Fecha', (r) => `${fechaCorta(r.fecha)}${r.hora ? ` ${r.hora}` : ''}`],
              ['OD', (r) => <b style={{ color: COLOR_OJO.OD }}>{r.od?.valor ?? '—'}</b>],
              ['OI', (r) => <b style={{ color: COLOR_OJO.OI }}>{r.oi?.valor ?? '—'}</b>],
              ['Paquimetría', (r) => (r.od?.paquimetria || r.oi?.paquimetria ? `${r.od?.paquimetria ?? '—'} / ${r.oi?.paquimetria ?? '—'} µm` : '—')],
              ['Método', (r) => r.metodo || '—'], ['Tratamiento', (r) => r.tratamiento || '—'],
              ['Por', (r) => <span className="text-xs text-muted-foreground">{r.created_by_name}</span>],
            ]} />
          </Seccion>
          <Seccion titulo="Campo visual" acciones={masCampos('campo_visual')}>
            {rapida({ tipo: 'campo_visual', titulo: 'Campo visual', testId: 'oft-rapida-cv',
              campos: [{ k: 'od.md', etiqueta: 'MD OD' }, { k: 'oi.md', etiqueta: 'MD OI' }, { k: 'sep', separador: true },
                { k: 'od.vfi', etiqueta: 'VFI OD' }, { k: 'oi.vfi', etiqueta: 'VFI OI' }] })}
            {de('campo_visual').length > 1 && (
              <GraficaOjos puntos={de('campo_visual').map((r) => ({ fecha: r.fecha, od: num(r.od?.md), oi: num(r.oi?.md) }))} unidad="dB" decimales={1} alto={200} />
            )}
            <Tabla filas={de('campo_visual')} {...comun} cols={[
              ['Fecha', (r) => fechaCorta(r.fecha)], ['Estrategia', (r) => r.estrategia],
              ['MD OD / OI', (r) => `${dec(r.od?.md)} / ${dec(r.oi?.md)} dB`], ['PSD OD / OI', (r) => `${dec(r.od?.psd)} / ${dec(r.oi?.psd)}`],
              ['VFI OD / OI', (r) => `${r.od?.vfi ?? '—'} / ${r.oi?.vfi ?? '—'} %`], ['Fiabilidad', (r) => r.fiabilidad],
            ]} />
          </Seccion>
          <Seccion titulo="Capa de fibras nerviosas (OCT)" acciones={masCampos('oct')}>
            {rapida({ tipo: 'oct', titulo: 'CFNR', unidad: 'µm', testId: 'oft-rapida-cfnr', campos: ojoNum('cfnr') })}
            {de('oct', (r) => r.od?.cfnr || r.oi?.cfnr).length > 1 && (
              <GraficaOjos puntos={de('oct', (r) => r.od?.cfnr || r.oi?.cfnr).map((r) => ({ fecha: r.fecha, od: num(r.od?.cfnr), oi: num(r.oi?.cfnr) }))} unidad="µm" alto={200} />
            )}
            <Tabla filas={de('oct', (r) => r.od?.cfnr || r.oi?.cfnr)} {...comun} cols={[
              ['Fecha', (r) => fechaCorta(r.fecha)], ['CFNR OD', (r) => r.od?.cfnr ?? '—'], ['CFNR OI', (r) => r.oi?.cfnr ?? '—'], ['Equipo', (r) => r.equipo],
            ]} />
          </Seccion>
        </TabsContent>

        <TabsContent value="refraccion" className="mt-4 space-y-4">
          <Seccion titulo="Refracciones" testId="oft-seccion-refraccion" acciones={masCampos('refraccion')}>
            {puedeEditar && (
              <EntradaRapida pid={pid} onGuardado={alGuardar} tipo="refraccion" testId="oft-rapida-refraccion"
                iniciales={{ clase: refrs[0]?.clase || 'subjetiva' }}
                campos={[
                  { k: 'clase', etiqueta: 'Tipo', opciones: Object.keys(CLASES_REFRACCION), nombres: Object.fromEntries(Object.entries(CLASES_REFRACCION).map(([k, t]) => [k, t.split(' (')[0]])) },
                  { k: 'od.esf', etiqueta: 'OD esf', ph: '−2,00' }, { k: 'od.cil', etiqueta: 'cil', ph: '−0,50' }, { k: 'od.eje', etiqueta: 'eje', ancho: 'w-12' },
                  { k: 'od.av_sc', etiqueta: 'AVsc', ancho: 'w-12' }, { k: 'od.av_cc', etiqueta: 'AVcc', ancho: 'w-12' },
                  { k: 'sep', separador: true },
                  { k: 'oi.esf', etiqueta: 'OI esf', ph: '−2,00' }, { k: 'oi.cil', etiqueta: 'cil', ph: '−0,50' }, { k: 'oi.eje', etiqueta: 'eje', ancho: 'w-12' },
                  { k: 'oi.av_sc', etiqueta: 'AVsc', ancho: 'w-12' }, { k: 'oi.av_cc', etiqueta: 'AVcc', ancho: 'w-12' },
                ]} />
            )}
            <Tabla filas={refrs} {...comun} cols={[
              ['Fecha', (r) => fechaCorta(r.fecha)],
              ['Tipo', (r) => <span className={r.clase === 'postoperatoria' ? 'font-semibold text-emerald-700' : ''}>{CLASES_REFRACCION[r.clase]?.split(' (')[0] || r.clase}</span>],
              ['OD', (r) => <span style={{ color: COLOR_OJO.OD }}>{refraccionTexto(r.od)}</span>], ['EE OD', (r) => dpt(r.od?.ee)],
              ['AV OD sc/cc', (r) => `${av(r.od?.av_sc)} / ${av(r.od?.av_cc)}`],
              ['OI', (r) => <span style={{ color: COLOR_OJO.OI }}>{refraccionTexto(r.oi)}</span>], ['EE OI', (r) => dpt(r.oi?.ee)],
              ['AV OI sc/cc', (r) => `${av(r.oi?.av_sc)} / ${av(r.oi?.av_cc)}`], ['Notas', (r) => r.notas || ''],
            ]} />
          </Seccion>
          <Seccion titulo="Plan de cirugía refractiva" acciones={boton('plan_refractivo', 'Nuevo plan')}>
            {de('plan_refractivo').length === 0 && <div className="text-sm text-muted-foreground">Sin plan refractivo.</div>}
            {de('plan_refractivo').map((p) => {
              const post = ultimo(regs, 'refraccion', (r) => r.clase === 'postoperatoria' && p.fecha_cirugia && r.fecha > p.fecha_cirugia);
              return (
                <div key={p.id} className="rounded-lg border p-3 mb-3" data-testid="oft-plan">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div className="font-semibold">{p.tecnica} {p.laser && <span className="text-muted-foreground font-normal">· {p.laser}</span>} · cirugía {fechaCorta(p.fecha_cirugia)} {p.cirujano && `· ${p.cirujano}`}</div>
                    {puedeEditar && <div><button type="button" className="p-1" onClick={() => editar(p)}><Pencil className="w-3.5 h-3.5" /></button><button type="button" className="p-1 hover:text-red-600" onClick={() => borrar(p)}><Trash2 className="w-3.5 h-3.5" /></button></div>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {OJOS.map(([o, O]) => {
                      const x = p[o] || {};
                      if (num(x.esf) === null) return null;
                      const residual = post?.[o];
                      const error = residual?.ee !== undefined && residual?.ee !== null ? residual.ee - (num(x.objetivo_ee) || 0) : null;
                      return (
                        <div key={o} className="text-sm space-y-0.5">
                          <div><Ojo o={O} /> A tratar: <b>{refraccionTexto(x)}</b> (EE {dpt(x.ee)}) · objetivo {dpt(x.objetivo_ee || 0)}</div>
                          <div className="text-xs">ZO {dec(x.zona_optica, 1)} mm · paquimetría {x.paquimetria ?? '—'} µm · colgajo {x.colgajo ?? 0} µm</div>
                          <div className="text-xs">Ablación <b>{x.ablacion_um ?? '—'} µm</b>{x.ablacion_calculada ? ' (Munnerlyn)' : ''} · lecho residual <b>{x.lecho_um ?? '—'} µm</b> · PTA <b>{x.pta ?? '—'} %</b></div>
                          {(x.avisos || []).map((a) => <div key={a} className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{a}</div>)}
                          {residual && <div className="text-xs">Residual ({fechaCorta(post.fecha)}): <b>{refraccionTexto(residual)}</b> · EE {dpt(residual.ee)} · AVsc {dec(residual.av_sc)} · <span className={Math.abs(error) <= 0.5 ? 'text-emerald-700' : 'text-amber-700'}>desvío {dpt(error)}</span></div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </Seccion>
          {avPuntos.length > 1 && (
            <Seccion titulo="Agudeza visual con corrección"><GraficaOjos puntos={avPuntos} alto={200} decimales={1} dominio={[0, 1.2]} marcas={[...cirugias, ...planes]} /></Seccion>
          )}
        </TabsContent>

        <TabsContent value="lio" className="mt-4 space-y-4">
          <Seccion titulo="Biometría" testId="oft-seccion-biometria" acciones={masCampos('biometria')}>
            {rapida({ tipo: 'biometria', testId: 'oft-rapida-biometria', campos: [
              { k: 'od.al', etiqueta: 'OD AL', ancho: 'w-16' }, { k: 'od.k1', etiqueta: 'K1' }, { k: 'od.k2', etiqueta: 'K2' }, { k: 'od.acd', etiqueta: 'ACD', ancho: 'w-14' },
              { k: 'sep', separador: true },
              { k: 'oi.al', etiqueta: 'OI AL', ancho: 'w-16' }, { k: 'oi.k1', etiqueta: 'K1' }, { k: 'oi.k2', etiqueta: 'K2' }, { k: 'oi.acd', etiqueta: 'ACD', ancho: 'w-14' },
            ] })}
            <Tabla filas={de('biometria')} {...comun} cols={[
              ['Fecha', (r) => fechaCorta(r.fecha)], ['Equipo', (r) => r.equipo || '—'],
              ...OJOS.map(([o, O]) => [`${O}: AL · K1/K2 · ACD`, (r) => (r[o]?.al ? <span style={{ color: COLOR_OJO[O] }}>{dec(r[o].al, 2)} mm · {dec(r[o].k1, 2)}/{dec(r[o].k2, 2)} D (Δ {dec(r[o].astig_corneal, 2)}) · {dec(r[o].acd, 2)} mm</span> : '—')]),
              ['WTW OD/OI', (r) => `${dec(r.od?.wtw)} / ${dec(r.oi?.wtw)}`],
            ]} />
          </Seccion>
          <Seccion titulo="Lentes intraoculares" testId="oft-seccion-lio" acciones={<>
            {boton('lio', 'LIO planificada', { fase: 'planificada' })}{boton('lio', 'LIO implantada', { fase: 'implantada' })}
          </>}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {OJOS.map(([, O]) => {
                const delOjo = lios.filter((l) => l.ojo === O);
                const impl = delOjo.find((l) => l.fase === 'implantada');
                const plan = delOjo.find((l) => l.fase === 'planificada');
                const post = impl && ultimo(regs, 'refraccion', (r) => r.clase === 'postoperatoria' && r.fecha > (impl.fecha_cirugia || impl.fecha));
                const res = post?.[O.toLowerCase()];
                const esperada = impl?.refraccion_esperada ?? plan?.refraccion_esperada;
                const err = res?.ee !== null && res?.ee !== undefined && esperada !== null && esperada !== undefined ? res.ee - esperada : null;
                return (
                  <div key={O} className="rounded-xl border p-3 text-sm space-y-1" style={{ borderColor: O === 'OD' ? '#FECACA' : '#BFDBFE' }} data-testid={`oft-lio-${O}`}>
                    <div className="flex items-center gap-2 font-semibold"><Ojo o={O} />{impl ? 'Implantada' : plan ? 'Planificada' : 'Sin LIO'}</div>
                    {[impl, plan].filter(Boolean).map((l) => (
                      <div key={l.id} className={`rounded-md p-2 ${l.fase === 'implantada' ? 'bg-emerald-50 dark:bg-emerald-950/30' : 'bg-muted/40'}`}>
                        <div className="flex justify-between"><b>{l.modelo || '—'}</b>
                          {puedeEditar && <span><button type="button" className="p-0.5" onClick={() => editar(l)}><Pencil className="w-3 h-3" /></button><button type="button" className="p-0.5 hover:text-red-600" onClick={() => borrar(l)}><Trash2 className="w-3 h-3" /></button></span>}
                        </div>
                        <div className="text-xs">{l.fase === 'implantada' ? `Implantada ${fechaCorta(l.fecha_cirugia || l.fecha)}${l.cirujano ? ` · ${l.cirujano}` : ''}` : `Cálculo ${fechaCorta(l.fecha)}`}</div>
                        <div className="text-xs">{l.tipo_lente} · <b>{dpt(l.potencia)}</b>{num(l.cilindro) ? ` · cil ${dpt(l.cilindro)} a ${l.eje}°` : ''} · {l.formula || '—'} (A {l.constante_a ?? '—'}) · esperada <b>{dpt(l.refraccion_esperada)}</b></div>
                        {l.fase === 'implantada' && (l.lote || l.posicion) && <div className="text-xs text-muted-foreground">{l.posicion} · lote {l.lote || '—'} · {l.incidencias || ''}</div>}
                      </div>
                    ))}
                    {res && (
                      <div className="text-xs pt-1" data-testid={`oft-residual-${O}`}>
                        Refracción residual ({fechaCorta(post.fecha)}): <b>{refraccionTexto(res)}</b> · EE <b>{dpt(res.ee)}</b> · AVsc {dec(res.av_sc)}
                        {err !== null && <div className={Math.abs(err) <= 0.5 ? 'text-emerald-700 font-semibold' : 'text-amber-700 font-semibold'}>Error de predicción {dpt(err)}</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Seccion>
        </TabsContent>

        <TabsContent value="cornea" className="mt-4 space-y-4">
          <Seccion titulo="Topografía: Kmax y paquimetría" testId="oft-seccion-topografia" acciones={masCampos('topografia')}>
            {rapida({ tipo: 'topografia', titulo: 'Topografía', testId: 'oft-rapida-topo', campos: [
              { k: 'od.kmax', etiqueta: 'Kmax OD' }, { k: 'oi.kmax', etiqueta: 'Kmax OI' }, { k: 'sep', separador: true },
              { k: 'od.paqui_min', etiqueta: 'Paqui. mín. OD' }, { k: 'oi.paqui_min', etiqueta: 'Paqui. mín. OI' }] })}
            {kmaxPuntos.length > 1 || paquiTopo.length > 1 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Kmax (D)</div>
                  <GraficaOjos testId="oft-grafica-kmax" puntos={kmaxPuntos} unidad="D" decimales={1} alto={200} />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Paquimetría mínima (µm)</div>
                  <GraficaOjos testId="oft-grafica-paqui" puntos={paquiTopo} unidad="µm" alto={200} />
                </div>
              </div>
            ) : <div className="text-sm text-muted-foreground">Las gráficas de Kmax y paquimetría salen con dos medidas o más (de aquí o de las topografías archivadas).</div>}
            <Tabla filas={de('topografia')} {...comun} vacio="Sin topografías escritas (las archivadas como imagen están abajo)." cols={[
              ['Fecha', (r) => fechaCorta(r.fecha)], ['Kmax OD / OI', (r) => `${dec(r.od?.kmax)} / ${dec(r.oi?.kmax)} D`],
              ['Paqui. mín. OD / OI', (r) => `${r.od?.paqui_min ?? '—'} / ${r.oi?.paqui_min ?? '—'} µm`], ['Notas', (r) => r.notas || ''],
            ]} />
          </Seccion>
          <Seccion titulo="Dibujo de la córnea" testId="oft-seccion-cornea">
            <DibujosOculares pid={pid} plantilla="cornea" registros={regs} puedeEditar={puedeEditar} onGuardado={alGuardar} onBorrar={borrar} />
          </Seccion>
          <ImagenesOftalmo pid={pid} imagenes={imagenes.filter((i) => ['cornea', 'topografia', 'segmento_anterior', 'parpados'].includes(i.categoria))}
            puedeEditar={puedeEditar} iaActiva={datos.ia_activa} onCambio={alCambiarImagen} categoriaInicial="cornea" />
        </TabsContent>

        <TabsContent value="retina" className="mt-4 space-y-4">
          <Seccion titulo="Dibujo del fondo de ojo" testId="oft-seccion-fondo">
            <DibujosOculares pid={pid} plantilla="fondo" registros={regs} puedeEditar={puedeEditar} onGuardado={alGuardar} onBorrar={borrar} />
          </Seccion>
          <Seccion titulo="Grosor macular central y tratamiento" testId="oft-seccion-retina" acciones={<>{masCampos('inyeccion')}</>}>
            <div className="flex flex-wrap gap-x-3">
              {rapida({ tipo: 'oct', titulo: 'Grosor central', unidad: 'µm', testId: 'oft-rapida-cst', campos: ojoNum('cst') })}
              {rapida({ tipo: 'inyeccion', titulo: 'Inyección', testId: 'oft-rapida-inyeccion',
                campos: [{ k: 'ojo', etiqueta: 'Ojo', opciones: ['OD', 'OI'] }, { k: 'farmaco', etiqueta: 'Fármaco', opciones: FARMACOS_INTRAVITREOS },
                  { k: 'lote', etiqueta: 'Lote (opcional)', ancho: 'w-24', texto: true }], sinNumeros: true,
                iniciales: { ojo: inys[0]?.ojo || 'OD', farmaco: inys[0]?.farmaco || FARMACOS_INTRAVITREOS[0] } })}
            </div>
            <GraficaOjos testId="oft-grafica-cst" puntos={cstPuntos} unidad="µm" marcas={inys.map((i) => ({ fecha: i.fecha, ojo: i.ojo }))} />
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Syringe className="w-3.5 h-3.5" />Las líneas verticales son las inyecciones (del color de su ojo).</div>
          </Seccion>
          <Seccion titulo={`Inyecciones intravítreas (${inys.length})`}>
            <Tabla filas={inys} {...comun} cols={[
              ['Fecha', (r) => fechaCorta(r.fecha)], ['Ojo', (r) => <Ojo o={r.ojo} />], ['Fármaco', (r) => r.farmaco],
              ['Indicación', (r) => r.indicacion], ['Nº en ese ojo', (r) => inys.filter((i) => i.ojo === r.ojo && i.fecha <= r.fecha).length],
              ['Intervalo', (r) => {
                const previa = inys.find((i) => i.ojo === r.ojo && i.fecha < r.fecha);
                return previa ? `${Math.round((new Date(r.fecha) - new Date(previa.fecha)) / 604800000)} sem.` : '—';
              }], ['Lote', (r) => r.lote || '—'],
            ]} />
          </Seccion>
          <ImagenesOftalmo pid={pid} imagenes={imagenes.filter((i) => ['oct_macula', 'retinografia', 'autofluorescencia', 'angiografia'].includes(i.categoria))}
            puedeEditar={puedeEditar} iaActiva={datos.ia_activa} onCambio={alCambiarImagen} categoriaInicial="oct_macula" />
        </TabsContent>

        <TabsContent value="imagenes" className="mt-4">
          <ImagenesOftalmo pid={pid} imagenes={imagenes} puedeEditar={puedeEditar} iaActiva={datos.ia_activa} onCambio={alCambiarImagen} />
        </TabsContent>
      </Tabs>

      {dialogo && (
        <RegistroDialog open={!!dialogo} onOpenChange={(o) => !o && setDialogo(null)} pid={pid} tipo={dialogo.tipo}
          registro={dialogo.registro} inicial={dialogo.inicial} onGuardado={alGuardar} />
      )}
      <FichaDialog open={fichaAbierta} onOpenChange={setFichaAbierta} pid={pid} ficha={ficha}
        onGuardada={(f) => setDatos((d) => ({ ...d, ficha: f }))} />
    </div>
  );
}
